import type { MediaDto } from '@/types/dtos/media/media.dto';
import { MEDIA_PARENT_ENTITY, type MediaParentEntity } from '@/types/dtos/media/media-parent-entity.enum';
import type { ProcessingInsightDto } from '@/types/dtos/media/processing-insight.dto';
import type { MediaProcessingInstructionsContext } from '@/types/media-processing-instructions.types';
import type { TranslationKey } from '@/i18n/translation-key';
import {
  resolveProcessingErrorMessage,
  resolveProcessingPolicyAdvisories,
  resolveProcessingPolicyViolationMessage,
  resolveProcessingPolicyViolations,
  resolveProcessingRejectionSummaryMessage,
  hasProcessingAdvisories,
  resolveProcessingLimitationKind,
} from '@/helpers/media-processing-policy-messages.helper';

type Translate = (key: TranslationKey, params?: Record<string, string | number>) => string;

const PARENT_COPY_KEY: Record<MediaParentEntity, TranslationKey> = {
  [MEDIA_PARENT_ENTITY.EVENT]: 'media.processing.instructionsModal.copyParentEvent',
  [MEDIA_PARENT_ENTITY.FLOW]: 'media.processing.instructionsModal.copyParentFlow',
  [MEDIA_PARENT_ENTITY.SESSION]: 'media.processing.instructionsModal.copyParentSession',
  [MEDIA_PARENT_ENTITY.ORGANIZATION]: 'media.processing.instructionsModal.copyParentOrganization',
};

const PARENT_LABEL_KEY: Record<MediaParentEntity, TranslationKey> = {
  [MEDIA_PARENT_ENTITY.EVENT]: 'media.processing.instructionsModal.contextEvent',
  [MEDIA_PARENT_ENTITY.FLOW]: 'media.processing.instructionsModal.contextFlow',
  [MEDIA_PARENT_ENTITY.SESSION]: 'media.processing.instructionsModal.contextSession',
  [MEDIA_PARENT_ENTITY.ORGANIZATION]: 'media.processing.instructionsModal.contextOrganization',
};

export function hasShareableProcessingInstructions(insight?: ProcessingInsightDto): boolean {
  if (!insight || insight.readiness === 'not_applicable') {
    return false;
  }
  if (insight.readiness === 'rejected' && insight.rejection) {
    return true;
  }
  if (hasProcessingAdvisories(insight)) {
    return true;
  }
  if (insight.readiness === 'failed') {
    return true;
  }
  if (insight.error?.message) {
    return true;
  }
  return false;
}

export function buildMediaProcessingInstructionsContext(params: {
  media: MediaDto;
  assetVersion?: number;
  eventTitle?: string;
  parentTitle?: string;
}): MediaProcessingInstructionsContext {
  const { media, assetVersion, eventTitle, parentTitle } = params;
  const assets = media.assets;
  const asset =
    assetVersion != null
      ? assets.find((item) => item.version === assetVersion)
      : assets.length > 0
        ? assets[assets.length - 1]
        : undefined;

  return {
    mediaTitle: media.title,
    assetVersion: asset?.version ?? assetVersion,
    eventTitle,
    parentType: media.parentType,
    parentTitle,
  };
}

export function parentContextLabelKey(parentType?: MediaParentEntity): TranslationKey | undefined {
  if (!parentType) {
    return undefined;
  }
  return PARENT_LABEL_KEY[parentType];
}

export function buildMediaProcessingInstructionsCopyText(
  context: MediaProcessingInstructionsContext,
  insight: ProcessingInsightDto,
  t: Translate,
): string {
  const violations = resolveProcessingPolicyViolations(insight);
  const advisories = resolveProcessingPolicyAdvisories(insight);
  const isAdvisoryOnly = advisories.length > 0 && violations.length === 0;

  const lines: string[] = [
    t(
      isAdvisoryOnly
        ? 'media.processing.instructionsModal.copyTitleReview'
        : 'media.processing.instructionsModal.copyTitle',
    ),
    '',
  ];

  if (context.eventTitle) {
    lines.push(t('media.processing.instructionsModal.copyEvent', { title: context.eventTitle }));
  }
  if (context.parentTitle && context.parentType) {
    lines.push(t(PARENT_COPY_KEY[context.parentType], { title: context.parentTitle }));
  }
  lines.push(t('media.processing.instructionsModal.copyMedia', { title: context.mediaTitle }));
  if (context.assetVersion != null) {
    lines.push(t('media.processing.instructionsModal.copyVersion', { version: context.assetVersion }));
  }

  lines.push('');
  if (advisories.length > 0 && violations.length === 0) {
    const limitationKind = resolveProcessingLimitationKind(advisories);
    const titleKey =
      limitationKind === 'display'
        ? 'media.processing.instructionsModal.copyLimitationsTitleDisplay'
        : limitationKind === 'playback'
          ? 'media.processing.instructionsModal.copyLimitationsTitlePlayback'
          : 'media.processing.instructionsModal.copyLimitationsTitleMixed';
    const introKey =
      limitationKind === 'display'
        ? 'media.processing.instructionsModal.copyLimitationsIntroDisplay'
        : limitationKind === 'playback'
          ? 'media.processing.instructionsModal.copyLimitationsIntroPlayback'
          : 'media.processing.instructionsModal.copyLimitationsIntroMixed';
    const footerKey =
      limitationKind === 'playback'
        ? 'media.processing.instructionsModal.copyLimitationsFooterRequired'
        : 'media.processing.instructionsModal.copyLimitationsFooterOptional';

    lines.push(t(titleKey));
    lines.push('');
    if (advisories.length > 1) {
      lines.push(t(introKey));
      lines.push('');
      for (const [index, advisory] of advisories.entries()) {
        lines.push(`${index + 1}. ${resolveProcessingPolicyViolationMessage(advisory, t)}`);
      }
    } else {
      lines.push(t(introKey));
      lines.push('');
      lines.push(resolveProcessingPolicyViolationMessage(advisories[0], t));
    }
    lines.push('');
    lines.push(t(footerKey));
    return lines.join('\n');
  }

  const summary = insight.rejection
    ? resolveProcessingRejectionSummaryMessage(violations, t)
    : insight.error
      ? resolveProcessingErrorMessage(insight.error, t)
      : t('media.processing.instructionsModal.copyStatusFallback');

  if (violations.length > 1) {
    lines.push(t('media.processing.instructionsModal.copySummary'));
    lines.push(summary);
    lines.push('');
    lines.push(t('media.processing.instructionsModal.copyFixes'));
    lines.push('');
    for (const [index, violation] of violations.entries()) {
      lines.push(`${index + 1}. ${resolveProcessingPolicyViolationMessage(violation, t)}`);
    }
    lines.push('');
  } else if (violations.length === 1) {
    lines.push(t('media.processing.instructionsModal.copyFixes'));
    lines.push('');
    lines.push(resolveProcessingPolicyViolationMessage(violations[0], t));
    lines.push('');
  } else if (summary) {
    lines.push(t('media.processing.instructionsModal.copySummary'));
    lines.push(summary);
    lines.push('');
  }

  lines.push(t('media.processing.instructionsModal.copyFooter'));
  return lines.join('\n');
}
